import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getTranslatedNaceSectorOptions } from "@/constants/naceSectors";
import { Building2, HelpCircle } from "lucide-react";
import { SectionCard } from "@/components/workflow/common/SectionCard";
interface LegalStructureFormProps {
  generalInfo: any;
  updateGeneralInfo: (updates: any) => void;
  isLocked?: boolean;
}

const fieldLabelClassName = "text-sm font-medium leading-5 text-slate-700";
const fieldInputClassName = "h-11 rounded-2xl border-slate-200 bg-slate-50 px-3 text-base text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-0 focus-visible:border-slate-300";
const fieldSelectClassName = "h-11 rounded-2xl border-slate-200 bg-slate-50 px-3 text-base text-slate-900 shadow-none data-[placeholder]:text-slate-400 focus:ring-0";

export function LegalStructureForm({ generalInfo, updateGeneralInfo, isLocked }: LegalStructureFormProps) {
  const naceOptions = getTranslatedNaceSectorOptions();

  const header = (
    <div className="flex min-w-0 items-center gap-2">
      <Building2 className="h-5 w-5 flex-shrink-0 text-[#00444C]" />
      <h3 className="text-lg font-bold leading-7 text-slate-900">{"Legal & Structure"}</h3>
      <HelpCircle className="h-4 w-4 flex-shrink-0 text-slate-600" />
    </div>
  );

  return (
    <SectionCard header={header}>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <Label className={fieldLabelClassName}>
            {"Legal Form"} <span className="text-red-500">*</span>
          </Label>
          <Select
            value={generalInfo.legalForm}
            onValueChange={(v) => updateGeneralInfo({ legalForm: v, legalFormSubtype: "" })}
            disabled={isLocked}
          >
            <SelectTrigger data-testid="select-legal-form" className={fieldSelectClassName}>
              <SelectValue placeholder={"Select legal form"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sole-proprietorship">{"Sole proprietorship"}</SelectItem>
              <SelectItem value="partnership">{"Partnership"}</SelectItem>
              <SelectItem value="private-limited">{"Private limited company (limited liability)"}</SelectItem>
              <SelectItem value="public-limited">{"Public limited company"}</SelectItem>
              <SelectItem value="cooperative">{"Cooperative"}</SelectItem>
              <SelectItem value="non-profit">{"Non-profit / Association / Foundation"}</SelectItem>
              <SelectItem value="state-public">{"State / Public authority"}</SelectItem>
              <SelectItem value="other">{"Other (specify)"}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {generalInfo.legalForm === "partnership" && (
          <div className="space-y-2">
            <Label className={fieldLabelClassName}>{"Partnership Type (optional)"}</Label>
            <Select
              value={generalInfo.legalFormSubtype}
              onValueChange={(v) => updateGeneralInfo({ legalFormSubtype: v })}
              disabled={isLocked}
            >
              <SelectTrigger data-testid="select-partnership-type" className={fieldSelectClassName}>
                <SelectValue placeholder={"Select type"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">{"General Partnership"}</SelectItem>
                <SelectItem value="limited">{"Limited Partnership"}</SelectItem>
                <SelectItem value="llp">{"LLP (Limited Liability Partnership)"}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {generalInfo.legalForm === "other" && (
          <div className="space-y-2">
            <Label className={fieldLabelClassName}>{"Specify Legal Form"}</Label>
            <Input
              placeholder={"Describe your legal form"}
              value={generalInfo.legalFormSubtype}
              onChange={(e) => updateGeneralInfo({ legalFormSubtype: e.target.value })}
              disabled={isLocked}
              className={fieldInputClassName}
            />
          </div>
        )}
        <div className="space-y-2">
          <Label className={fieldLabelClassName}>
            {"Ownership Structure"} <span className="text-red-500">*</span>
          </Label>
          <Select
            value={generalInfo.ownershipStructure}
            onValueChange={(v) => updateGeneralInfo({ ownershipStructure: v })}
            disabled={isLocked}
          >
            <SelectTrigger data-testid="select-ownership" className={fieldSelectClassName}>
              <SelectValue placeholder={"Select ownership structure"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="family-owned">{"Family-owned"}</SelectItem>
              <SelectItem value="employee-owned">{"Employee-owned"}</SelectItem>
              <SelectItem value="investor-backed">{"Investor-backed"}</SelectItem>
              <SelectItem value="government-owned">{"Government-owned"}</SelectItem>
              <SelectItem value="cooperative-owned">{"Cooperative-owned"}</SelectItem>
              <SelectItem value="publicly-traded">{"Publicly traded"}</SelectItem>
              <SelectItem value="mixed">{"Mixed ownership"}</SelectItem>
              <SelectItem value="other">{"Other"}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label className={fieldLabelClassName}>
            {"Sector (NACE)"} <span className="text-red-500">*</span>
          </Label>
          <select
            disabled={isLocked}
            value={generalInfo.naceSector || ""}
            onChange={(e) => updateGeneralInfo({ naceSector: e.target.value })}
            className="flex h-11 w-full items-center justify-between whitespace-nowrap rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-base text-slate-900 shadow-none ring-offset-background placeholder:text-slate-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="select-nace-sector"
          >
            <option value="">{"Select NACE sector"}</option>
            {naceOptions.map((sector) => (
              <option key={sector.code} value={sector.code}>
                {sector.code} - {sector.translatedLabel}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">Select the category that best describes your main business activity. If you&apos;re unsure, choose the closest match.</p>
        </div>
      </div>
    </SectionCard>
  );
}
